import * as XLSX from "xlsx";

const n = (v) => (v == null || Number.isNaN(Number(v)) ? "" : Number(v));

/**
 * Builds the "SKU Detail & Coverage" export rows from a forecast artifact's
 * `data.skus` + `narrative.skus`, matching the columns shown in the
 * SKU Detail & Coverage table on the product analysis (ProductArtifactSections
 * / forecastArtifactHtml render the same fields from the same shape).
 */
export const buildSkuCoverageSheetRows = (data, narrative) => {
  const skus = data?.skus || [];
  const bySku = new Map((narrative?.skus || []).map((s) => [s.sku, s]));

  // One "<warehouse> Units" column per warehouse (by _id — client data can
  // have two active warehouses sharing the same display name, e.g. two
  // Pipe17 locations both called "Harrisburg", which would otherwise
  // silently merge their units into one column) any SKU was allocated to.
  const byId = new Map();
  skus.forEach((s) =>
    (s.warehouse_allocation || []).forEach((a) => {
      const id = String(a.warehouse ?? "");
      if (id && !byId.has(id)) byId.set(id, a.name || "Warehouse");
    }),
  );
  // Disambiguate only the names that actually collide across different
  // _ids — the common case (every name distinct) keeps a plain header.
  const nameCounts = new Map();
  byId.forEach((name) => nameCounts.set(name, (nameCounts.get(name) || 0) + 1));
  const warehouses = [...byId.entries()].map(([id, name]) => ({
    id,
    header:
      nameCounts.get(name) > 1 ? `${name} (#${id.slice(-6)})` : name,
  }));

  return skus.map((s) => {
    const nar = bySku.get(s.sku) || {};
    const allocated = Object.fromEntries(
      warehouses.map(({ id, header }) => [
        `${header} Units`,
        n(
          (s.warehouse_allocation || []).find(
            (a) => String(a.warehouse ?? "") === id,
          )?.forecast_units ?? 0,
        ),
      ]),
    );
    return {
      SKU: s.sku,
      Inventory: n(s.inventory?.on_hand),
      "Total Demand": n(s.demand?.total),
      "Avg Mo. Demand": n(s.demand?.avg_monthly),
      "Mo. Coverage": n(s.coverage_months),
      Status: nar.status || "",
      "Capital at Cost": n(s.capital_at_cost),
      ...allocated,
      Recommendation: nar.recommendation || "",
    };
  });
};

export const downloadSkuCoverageXlsx = (
  data,
  narrative,
  fileName = "sku-detail-coverage.xlsx",
) => {
  const rows = buildSkuCoverageSheetRows(data, narrative);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(rows),
    "SKU Detail & Coverage",
  );
  XLSX.writeFile(workbook, fileName);
};
